import { CryptoService, SettingsAttributes } from '@pulse/common';

export class SettingsHelper {
    constructor(private crypto: CryptoService) {}

    private getAttributesFromQuery(attributesFromQuery: string): SettingsAttributes {
        const attributes: SettingsAttributes = {};

        attributesFromQuery.split(',').forEach((pair) => {
            const [key, value] = pair.split('.');
            attributes[key] = value;
        });

        return attributes;
    }

    getPulseScript(): URLSearchParams | null {
        if (document?.currentScript instanceof HTMLScriptElement) {
            return new URLSearchParams(document?.currentScript.src);
        } else {
            const scripts = document.getElementsByTagName('script');

            for (let i = 0; i < scripts.length; i++) {
                const script = scripts[i];
                const src = script.getAttribute('src');

                if (
                    src &&
                    src.match(
                        /^(https?|http):\/\/[^/]+\/pulse\/[^/]+\/(module\/|nomodule\/)?pulse\.js/i,
                    )
                ) {
                    return new URLSearchParams(src);
                }
            }
        }

        return null;
    }

    getSettingsAttributes(url: URLSearchParams): SettingsAttributes {
        const attributesFromQuery = url.get('attr');

        if (!attributesFromQuery) {
            return {};
        } else {
            return this.getAttributesFromQuery(attributesFromQuery);
        }
    }

    shouldReturnDefaultValue(
        remoteSettingEnabled: boolean,
        firstCallState: { firstCallDone: boolean; defaultReturned: boolean },
        settingName: string,
        tag: string,
        cache?: { cachedSetting: Record<string, any>; timedOut: boolean },
    ): boolean {
        // If remote setting is disabled, always return default
        if (!remoteSettingEnabled) {
            return true;
        }

        // If first call hasn't completed and default was returned, use default
        if (firstCallState.firstCallDone && firstCallState.defaultReturned) {
            return true;
        }

        const emptyForTags: string[] =
            typeof cache?.cachedSetting['emptyForTags'] === 'string'
                ? cache.cachedSetting['emptyForTags'].split(',')
                : [];

        if (
            !cache?.timedOut &&
            cache?.cachedSetting[settingName] === undefined &&
            emptyForTags.includes(tag)
        ) {
            return true;
        }

        return false;
    }

    getAttributesHash({
        tag,
        attributes,
        userRole,
        isTest,
        userId,
        tierId,
        deviceLastTierId,
        debugExperiment,
        appVersion,
    }: {
        tag: string;
        attributes: SettingsAttributes;
        userRole: string;
        isTest: string;
        userId: string;
        tierId: string;
        deviceLastTierId: string;
        debugExperiment: string;
        appVersion: string;
    }): string {
        return this.crypto.unsafeShortHash(
            `${tag}-${userRole}-${isTest}-${userId}-${tierId}-${deviceLastTierId}-${debugExperiment}-${JSON.stringify(
                attributes,
            )}-${appVersion}`,
        );
    }
}
